import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { HelpCircle, BookOpen, Wrench, Lightbulb, ListChecks, ExternalLink, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { getHelpFor } from '../../help/helpContent';

const Section = ({ icon: Icon, title, children, color = '#FF6B4A' }) => (
  <div>
    <h3 className="text-sm font-bold text-stone-900 flex items-center gap-2 mb-1.5">
      <Icon className="w-4 h-4" style={{ color }} />
      {title}
    </h3>
    <div className="text-sm text-stone-700 leading-relaxed pl-6">{children}</div>
  </div>
);

const FeatureHelp = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const entry = getHelpFor(location.pathname);

  // Hide on auth / public pages where help adds noise.
  const hiddenOn = ['/login', '/register', '/'];
  if (hiddenOn.includes(location.pathname)) return null;
  if (!entry) return null;

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="fixed top-4 right-4 z-30 w-10 h-10 rounded-full bg-white border border-stone-200 shadow-sm hover:shadow-md hover:border-[#FF6B4A] flex items-center justify-center transition group"
        title={`Ayuda: ${entry.title}`}
        aria-label={`Ayuda de ${entry.title}`}
        data-testid="feature-help-button"
      >
        <HelpCircle className="w-5 h-5 text-stone-500 group-hover:text-[#FF6B4A] transition" strokeWidth={1.8} />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="feature-help-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl font-heading flex items-center gap-2">
              <span className="inline-block w-2 h-6 bg-[#FF6B4A] rounded-full" />
              {entry.title}
            </DialogTitle>
            {entry.tagline && (
              <DialogDescription className="text-sm text-stone-500 mt-1">{entry.tagline}</DialogDescription>
            )}
          </DialogHeader>

          <div className="space-y-5 mt-3">
            {entry.what && (
              <Section icon={BookOpen} title="¿Qué es?" color="#3B82F6">
                <p>{entry.what}</p>
              </Section>
            )}

            {entry.how?.length > 0 && (
              <Section icon={Wrench} title="¿Cómo se usa?" color="#10B981">
                <ol className="list-decimal pl-4 space-y-1">
                  {entry.how.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ol>
              </Section>
            )}

            {entry.tips?.length > 0 && (
              <Section icon={Lightbulb} title="Tips y atajos" color="#F59E0B">
                <ul className="list-disc pl-4 space-y-1">
                  {entry.tips.map((tip, i) => (
                    <li key={i}>{tip}</li>
                  ))}
                </ul>
              </Section>
            )}

            {entry.useCases?.length > 0 && (
              <Section icon={ListChecks} title="Casos de uso" color="#8B5CF6">
                <ul className="list-disc pl-4 space-y-1">
                  {entry.useCases.map((uc, i) => (
                    <li key={i}>{uc}</li>
                  ))}
                </ul>
              </Section>
            )}

            {entry.related?.length > 0 && (
              <Section icon={ExternalLink} title="Relacionado" color="#EC4899">
                <div className="flex flex-wrap gap-2">
                  {entry.related.map((r, i) => (
                    <Link
                      key={i}
                      to={r.path}
                      onClick={() => setOpen(false)}
                      className="text-xs px-2.5 py-1 rounded-full border border-stone-200 hover:border-[#FF6B4A] hover:text-[#FF6B4A] transition"
                    >
                      {r.label}
                    </Link>
                  ))}
                </div>
              </Section>
            )}

            <p className="text-[11px] text-stone-400 pt-3 border-t border-stone-100">
              ¿Echas algo en falta en esta ayuda? Edita <code>/app/frontend/src/help/helpContent.js</code>.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FeatureHelp;
